import { Clock } from "lucide-react";
import { FadeIn } from "./FadeIn";
import { BOOKING_URL, SERVICES } from "../data/content";

const THEMES = {
  red: "hover:border-red-500/50 hover:shadow-[0_0_20px_rgba(239,68,68,0.15)]",
  green:
    "hover:border-emerald-500/50 hover:shadow-[0_0_20px_rgba(16,185,129,0.15)]",
  yellow:
    "hover:border-amber-500/50 hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]",
  rainbow:
    "hover:border-transparent relative before:absolute before:inset-0 before:p-[1px] before:bg-gradient-to-r before:from-red-500 before:via-amber-500 before:to-emerald-500 before:-z-10 before:rounded-2xl before:opacity-50 hover:before:opacity-100 before:transition-opacity",
};

const DOTS = {
  red: "bg-red-500",
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  rainbow: "bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500",
};

export const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="px-4 sm:px-8 lg:px-12 py-16 md:py-24 max-w-7xl mx-auto"
  >
    <FadeIn className="flex flex-col gap-4 mb-14">
      <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.3em]">
        Signature Menu
      </p>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">
        Signature Services &amp; Packages
      </h2>
      <p className="text-white/70 text-base md:text-lg font-light max-w-2xl">
        Transparent pricing. No hidden charges. Pure indulgence.
      </p>
    </FadeIn>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {SERVICES.map((s, i) => (
        <FadeIn key={s.name} delay={i * 0.08}>
          <div
            data-testid={`service-card-${s.theme}`}
            className={`h-full bg-[#0c0c0c] rounded-2xl border border-white/10 overflow-hidden flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 group ${THEMES[s.theme]}`}
          >
            <div>
              {/* Full Uncropped Service Image */}
              {s.image && (
                <div className="w-full relative overflow-hidden bg-black/40 border-b border-white/10">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="w-full h-auto block object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}

              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2.5">
                  <span className={`w-3 h-3 rounded-full ${DOTS[s.theme]}`} />
                  <h3 className="font-serif text-xl font-bold text-white leading-snug">
                    {s.name}
                  </h3>
                </div>

                <div className="flex items-end justify-between border-b border-white/10 pb-3">
                  <p
                    data-testid={`service-price-${s.theme}`}
                    className="font-serif text-3xl font-black bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600 bg-clip-text text-transparent"
                  >
                    {s.price}
                  </p>
                  <span className="flex items-center gap-1 text-white/60 text-xs font-bold uppercase tracking-wider bg-white/5 px-3 py-1 rounded-full border border-white/10">
                    <Clock className="w-3.5 h-3.5 text-amber-400" /> {s.duration}
                  </span>
                </div>

                <p className="text-white/70 text-xs font-normal leading-relaxed">
                  {s.desc}
                </p>

                {s.highlights && s.highlights.length > 0 && (
                  <ul className="space-y-1.5 pt-2 text-[11px] text-white/80">
                    {s.highlights.map((h, hIdx) => (
                      <li key={hIdx} className="flex items-start gap-2">
                        <span className="text-amber-400 font-bold">✓</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="p-6 pt-0">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-book-btn-${s.theme}`}
                className="block text-center border border-amber-400/50 text-amber-300 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.2em] hover:bg-gradient-to-r hover:from-amber-300 hover:to-amber-600 hover:text-black transition-all duration-300 shadow-md"
              >
                Reserve Session
              </a>
            </div>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);
